/**
 * ES6 module
 * Grid Export module
 * controls grid export server calls
 * ©2021 CoreStream Ltd
 *
 * @format
 *
 * API end points
 * StartGridExportBackgroundService - same payload as current ExportGridData - returns exportJobId
 * CheckExportStatus/ {exportJobId} - returns the state as a String - Enqueued/Scheduled/Awaiting/Processing/Failed/Succeeded/Deleted
 * GetExportFile/{exportJobId}/{exportTemplateId}/{exportFileName} - returns the export file object
 *
 * http://vnextlocal.corestream.co.uk:5003/Compliance-Advisory-Area/CA-Regulation All Active Regulation tab
 *
 */

/* eslint no-console: 0, eqeqeq: 0 */

/*
ES6 modules should conform to the following rules 
i) They should not contain permament references to external objects - i.e. event listeners/DOM nodes/Vue reactive data - $cs_.deepClone() Objects to ensure pointers are not sent
ii) Passing temporary references as parameters is acceptable but be careful not to create closures 
iii) ES6 module declared objects and properties must be mutated inside the module through custom module function calls 
*/

//INTERFACE
//IMPORTS
import $cs_ from '@/js/plugins/cs-underscore-helpers';
import $csNetworkCall from '@/js/modules/csNetworkCall.js';
import {$csI18n} from '@/js/modules/csI18n.js';

const _translationExports = $csI18n.t('exports');

//CLASSES

class ExportPayload {
	constructor(payloadObject) {
		this.collectionId = payloadObject.collectionId;
		this.colours = null;
		this.configNotes = null;
		this.documentTitle = payloadObject.documentTitle;
		this.enduserExportTemplate = null;
		this.exportWordFromForm = false;
		this.fieldCollection = payloadObject.fieldCollection;
		this.filesToUpload = null;
		this.footerText = null;
		this.generateAsPdf = false;
		this.headerText = null;
		this.id = payloadObject.id;
		this.isEndUserCreated = true;
		this.logoHeight = null;
		this.logoPath = null;
		this.logoWidth = null;
		this.mergeLinkedItemCells = payloadObject.mergeLinkedItemCells;
		this.name = payloadObject.name;
		this.owner = payloadObject.owner ?? null;
		this.pageOrientation = payloadObject.pageOrientation;
		this.printSize = payloadObject.printSize;
		this.queryTemplateId = null;
		this.siteFormIds = [];
		this.tags = [];
		this.translations = null;
		this.type = payloadObject.type;
		this.wordFieldCollection = null;
		this.wordFileName = null;
		this.wordLocaleTemplates = null;
		this.wordTemplate = null;
	}
}

export class ExportFormModelClass {
	#formsArray;
	#linearForm;
	#exportFileData = null;
	#debounceFieldUpdate = new Map();
	#debounceFieldUpdateTimeout;
	//#globalConditionals;
	constructor(formsArray, formObjectData) {
		this.#formsArray = formsArray;
		this.globalConditionals = $cs_.deepClone(formObjectData.conditionals);
		this.globalConditionals.isSaveRequired = true;
		this.globalConditionals.isSaveReady = false;
		this.globalConditionals.isReadOnly = false;

		this.isAllFormsValid = false;
		this.collectionId = formObjectData.collectionId;
		this.exportId = null;
		this.updateValue();
	}
	/**
	 * updates the linear form
	 */
	#updateFormModel() {
		this.#linearForm = new Map();

		for (let f = 0; f < this.#formsArray.length; f++) {
			let form = this.#formsArray[f];
			for (let i = 0; i < form.fields.length; i++) {
				let field = form.fields[i];

				if (this.#linearForm.has(field.id) === false) this.#linearForm.set(field.id, []);
				let idArray = this.#linearForm.get(field.id);
				idArray.push(field);
			}
		}
	}
	/**
	 * returns the private linear form - use locally in Vue, ensure it is off Vue reactivity
	 * Maps are NOT supported by Vue
	 */
	getLinearForm() {
		return this.#linearForm;
	}
	/**
	 * gets a field by Id - always returns the first match
	 * TODO if we ever need to get duplicate ids - i.e. subform ones consider expanding logic
	 * @param {String} id
	 * @returns
	 */
	getExportFieldInFormById(id) {
		return this.#linearForm.get(id)?.[0];
	}
	/**
	 * sets the export Id for the forms
	 */
	setExportId(id) {
		this.exportId = id;
	}
	setGlobalState(key, value) {
		this.globalConditionals[key] = value;
		this.updateValue();
	}
	/**
	 *sets the export file data
	 @param {Object} exportFileData
	 */
	setExportFileData(exportFileData) {
		this.#exportFileData = exportFileData;
	}
	/**
	 * returns the export file data
	 * @returns {Object}
	 */
	getExportFileData() {
		return this.#exportFileData;
	}
	/**
	 * Update the field value with the newValue and call updateForms
	 * @param {Object} field
	 * @param {*} value
	 */
	updateFieldValue(field, value, formId) {
		field.value = value;
		//TODO call updateForms debounced and with a Map of fields/forms being updated
	}
	updateValue(value, field = {}, formId) {
		field.value = value;

		this.#debounceFieldUpdate.set(field?.id ?? 'updateForm', {fieldId: field?.id, formId: formId});
		if (this.#debounceFieldUpdateTimeout != undefined) clearTimeout();
		this.#debounceFieldUpdateTimeout = setTimeout(() => {
			this.#updateFormModel();
			this.#debounceFieldUpdate.forEach((field, key) => {
				this.updateForms(field.fieldId, field.formId);
			});
			this.#debounceFieldUpdateTimeout = undefined;
			this.#debounceFieldUpdate.clear();
			this.#validateFormAndSetVisibility();
		}, 300);
	}
	#validateFormAndSetVisibility() {
		//Update the linear form model - run every time for now

		//visibility
		this.#linearForm.forEach((idArray) => {
			for (let field of idArray) {
				if (Array.isArray(field?.conditional?.conditionCriteria?.[0]?.conditions) === true) {
					//hardcoded support for conditionCriteria[0].logicalOperator "AND" .i.e break loop on first false
					this.#setVisibility(field, field.conditional.conditionCriteria[0].conditions);
				}
			}
		});
		//TODO move this into the linearForm visibility check above, but we will need to apply a formGuid and a back reference to the formGuid
		let allFormsValid = true;
		for (let f = 0; f < this.#formsArray.length; f++) {
			let form = this.#formsArray[f];
			form.isValid = true;
			for (let i = 0; i < form.fields.length; i++) {
				let field = form.fields[i];
				if (field?.validationRules?.required === true && field.isVisible === true) {
					let isFieldEmpty = field.value == undefined || (Array.isArray(field.value) && field.value.length === 0);
					if (isFieldEmpty === true) form.isValid = false;
				}
			}
			if (this.#formsArray?.[f - 1]?.isValid === false) form.isValid = false;
			if (form.id === 'save' && (this.exportId == undefined || this.globalConditionals.isSaveRequired === true || this.globalConditionals.saveInProgress === true || this.globalConditionals.emptyLinkedContent === true)) form.isValid = false;
			if (form.isValid === false) allFormsValid = false;
			//ALERTS
			if (Array.isArray(form.alerts) === true) {
				for (let i = 0; i < form.alerts.length; i++) {
					let alert = form.alerts[i];
					if (Array.isArray(alert?.conditional?.conditionCriteria?.[0]?.conditions) === true) {
						this.#setVisibility(alert, alert.conditional.conditionCriteria[0].conditions);
					}
				}
			}
		}

		this.isAllFormsValid = allFormsValid;

		//set the export type of the form based on the value derived by the heirachy — useExistingTemplate?.type ?? saveAsExistingTemplate?.type ?? customTemplate;
		let useExistingTemplate = this.getExportFieldInFormById('use-existing-export')?.value?.exportTemplate;
		let customTemplate = this.getExportFieldInFormById('export-type')?.value;

		let newExportType = useExistingTemplate?.type ?? customTemplate;
		//if the exportType changes wipe the options from the saveExistingExport fiels because we now filter options for type
		if (this.globalConditionals.exportType !== newExportType) {
			let saveExistingExport = this.getExportFieldInFormById('save-existing-export');
			saveExistingExport.definition.options = [];
			saveExistingExport.value = null;
			this.globalConditionals.exportType = newExportType;
		}
	}
	/**
	 * @public
	 * @description
	 * updatesVisibility based on conditional object
	 */
	updateForms(updatedFieldId, formId) {
		if (formId === 'customise-template') {
			this.setExportId(null);
			this.setGlobalState('isSaveRequired', true);
		}

		if (updatedFieldId === 'which-fields') {
			let choiceField = this.getExportFieldInFormById('root-field-choicelist');
			let prepareYouExport = this.#formsArray.find((v) => v.id === 'prepare-your-export');
			let collectionColumnFieldValues = $csGridExport.getColumnFieldsForm(this.collectionId);
			let whichFields = prepareYouExport.fields.find((v) => v.id === 'which-fields');

			if (whichFields.value?.value === 'allFieldsColumnsInCollection') {
				choiceField.value = collectionColumnFieldValues.allColumnFieldsProject;
			} else if (whichFields.value?.value === 'allFieldsColumnsInGrid') {
				choiceField.value = collectionColumnFieldValues.allColumnFieldsGrid;
			} else if (whichFields.value?.value === 'customSetOfFields') {
				choiceField.value = null;
			} else if (whichFields.value?.value === 'existingExport') {
				choiceField.value = null;
			}

			let saveExistingExportField = this.getExportFieldInFormById('save-existing-export');
			let useExistingField = this.getExportFieldInFormById('use-existing-export');
			saveExistingExportField.definition.options = [];
			saveExistingExportField.value = null;
			useExistingField.definition.options = [];
			useExistingField.value = null;
			this.setGlobalState('isSaveRequired', true);
			this.globalConditionals.exportId = null;
			this.setGlobalState('isReadOnly', false);
			this.setExportFileData(null);
			this.setExportId(null);
		}

		if (updatedFieldId === 'use-existing-export') {
			let useExistingField = this.getExportFieldInFormById('use-existing-export');
			let choiceField = this.getExportFieldInFormById('root-field-choicelist');
			choiceField.value = useExistingField?.value?.choiceFields ?? null;

			this.setExportFileData(useExistingField?.value?.exportTemplate ?? null);
			this.setExportId(useExistingField?.value?.exportTemplate?.id ?? null);
			if (useExistingField.value != null) {
				this.setGlobalState('isSaveRequired', false);
				if (useExistingField?.value?.exportTemplate?.isReadOnly === false && useExistingField?.value?.exportTemplate?.isEndUserCreated === true) this.setGlobalState('isReadOnly', false);
				else this.setGlobalState('isReadOnly', true);
			} else {
				this.setGlobalState('isSaveRequired', true);
				this.setGlobalState('isReadOnly', false);
			}
			let saveExistingNew = this.getExportFieldInFormById('save-existing-new');
			if (saveExistingNew != undefined) saveExistingNew.value = null;
		}
		if (updatedFieldId === 'save-existing-new') {
			let saveExistingNew = this.getExportFieldInFormById('save-existing-new');
			let saveExistingExportField = this.getExportFieldInFormById('save-existing-export');
			let saveNewTemplate = this.getExportFieldInFormById('save-new-template');

			if (saveExistingNew.value == undefined) this.setGlobalState('saveInProgress', undefined);
			else {
				this.setGlobalState('saveInProgress', true);
				if (saveExistingNew.value === 'save as new') this.setGlobalState('isSaveReady', saveNewTemplate.value == undefined ? false : true);
				else this.setGlobalState('isSaveReady', saveExistingExportField.value == undefined ? false : true);
			}
		}

		//TODO programatic change will fire watch of both
		if (updatedFieldId === 'save-existing-export') {
			let saveExistingExportField = this.getExportFieldInFormById('save-existing-export');

			this.setGlobalState('isSaveReady', saveExistingExportField.value == undefined ? false : true);
			this.setGlobalState('saveInProgress', true);
		}
		if (updatedFieldId === 'save-new-template') {
			let saveNewTemplate = this.getExportFieldInFormById('save-new-template');

			this.setGlobalState('isSaveReady', saveNewTemplate.value == undefined ? false : true);
			this.setGlobalState('saveInProgress', true);
		}

		if (updatedFieldId === 'which-data') {
			let whichDataField = this.getExportFieldInFormById('which-data');
			let fileStructureField = this.getExportFieldInFormById('file-structure');

			if (whichDataField?.value?.value === 'selectedDataRows') {
				if (this.globalConditionals.totalRows > 100) fileStructureField.definition.options[0].isDisabled = true;
				else fileStructureField.definition.options[0].isDisabled = false;
			} else if (whichDataField?.value?.value === 'allDataWithPermission') {
				if (this.globalConditionals.totalUnfilteredRows > 100) fileStructureField.definition.options[0].isDisabled = true;
				else fileStructureField.definition.options[0].isDisabled = false;
			} else {
				fileStructureField.definition.options[0].isDisabled = false;
			}
			//remove the value if option is disabled field
			if (fileStructureField.definition.options[0].isDisabled === true && fileStructureField.value === fileStructureField.definition.options[0].value) {
				fileStructureField.value = null;
			}
		}
	}

	/**
	 *
	 * @param {*} fieldValue
	 * @param {*} valueToMeet
	 * @param {Object} condition
	 */
	#setVisibility(field, conditions) {
		for (let condition of conditions) {
			let normalisedFieldValue = null;
			let fieldPathArray = condition.columnId.split('.');

			if (fieldPathArray[0] === 'globalConditionals') {
				normalisedFieldValue = fieldPathArray.reduce((acc, cur) => acc?.[cur], this) ?? null;
			} else {
				let targetField = this.#linearForm.get(condition.columnId)?.[0];
				//skip visibility check against non-visible fields
				if (targetField.isVisible === false) {
					field.isVisible = true;
					continue;
				}
				normalisedFieldValue = targetField?.value?.value != undefined ? targetField?.value?.value : targetField?.value != undefined ? targetField?.value : null;
			}
			switch (condition.conditionType) {
				case 'Equal':
					if (condition.valueToMeet === normalisedFieldValue) field.isVisible = true;
					else field.isVisible = false;
					break;
				case 'NotEqual':
					if (condition.valueToMeet === normalisedFieldValue) field.isVisible = false;
					else field.isVisible = true;
					break;
				case 'GreaterThan':
					if (condition.valueToMeet < normalisedFieldValue) field.isVisible = true;
					else field.isVisible = false;
					break;
				case 'LessThan':
					if (condition.valueToMeet > normalisedFieldValue) field.isVisible = true;
					else field.isVisible = false;
					break;
			}
			//ONLY HANDLE `AND` checks for operatorType at present - break is hardcoded
			if (field.isVisible === false) break;
		}
	}
}

//IMPLEMENTATION
//PRIVATE
let _gridExportGuid = 5;
let _urlBase = '';
let _collectionCache = new Map();
let _columnOptionsCache = new Map();
const _collectionDisplayCache = new Map();
const _collectionLookupCache = new Map();

//gridExportCustomiseForms
const _columnFieldFormsCache = new Map();

//METHODS
const _csGridExport = {};
/**
 * @public
 * @description
 * gets the file form the API call GetExportFile
 * calls the saveExportedFile method once the file is returned
 * @param {Object} download
 */
_csGridExport.downloadExportFile = async (download) => {
	try {
		let payload = {exportJobId: download.exportJobId, exportTemplateId: download.exportId, fileName: download.fileName};
		let networkGetExportFileGuid = $csNetworkCall.createFetchPostRequest(`${_urlBase}/api/GenericObjectController/GenerateOneTimeAccessToken`, payload);
		let token = await $csNetworkCall.fetchRequest(networkGetExportFileGuid);
		await token.data;
		let fileUrl = `${_urlBase}/api/GenericObjectController/GetExportFile/${token.data}`;
		window.location = fileUrl;
		return true;
	} catch (error) {
		return false;
	}
};

/**
 * @public
 * @description
 * consume the promises separate to their creation and asynchronously
 * possible export states Enqueued / Scheduled / Awaiting / Processing / Failed / Succeeded / Deleted;
 * @param {Number} i
 */
_csGridExport.consumeExportPromise = async (pendingExport) => {
	let response = await $csNetworkCall.fetchRequest(pendingExport.networkExportStatusGuid);
	let result;

	if (response != undefined) {
		let exportState = response.data;
		if (pendingExport != undefined) {
			switch (exportState) {
				//case 'Enqueued':
				case 'Succeeded':
					result = 'Succeeded';
					break;
				case 'Failed':
				case 'Deleted':
					result = 'Failed';
					break;
			}
		}
	}
	return result;
};

/**
 * creates and returns an empty fieldType object
 */
_csGridExport.createEmptySelectFieldObject = () => {
	return {
		'id': 'field-select-inline',
		'isVisible': true,
		'label': null, //`${_translationExports.chooseField}`,
		'value': null,
		'validationRules': {'required': true},
		'definition': {
			'fieldType': 'cs-select',
			'options': []
		}
	};
};

/**
 * TODO move this logic to the BE because we currently need to pass gridAPI data into this method
 * this will allow the commented out code above to work using a LoadOnDemand methodology
 * currently we need to decorate all the forms before the export window can open
 * converts column collection data into field data
 * @param {Array} data
 * @param {String} collectionId
 * @param {Array} gridAPIMap
 * @returns {Array}
 */
_csGridExport.getColumnOptionsFromCollectionData = (data, collectionId, gridAPIMap) => {
	let columnOptions = [];
	//create a lowercase Map of the data items
	for (let i = 0; i < data.length; i++) {
		let item = data[i];
		if (item.fieldCollectionConfig.hideFromPicker === false) {
			let itemOptions = null;
			//TODO - hardcoded options for now based on isComment and lookupCollectionId

			let fieldType = item.fieldCollectionConfig.isRepeater ? 'Repeater' : 'Core';

			if (item.fieldCollectionConfig.isComment === true || item.fieldCollectionConfig.lookupCollectionId != undefined) {
				let field = $cs_.deepClone(_csGridExport.createEmptySelectFieldObject());

				field.value = null;
				//Show as Lookup is Link content - Show as text is a Lookup using the displayTemplateField
				if (item.fieldCollectionConfig.isComment === true) {
					field.definition.options.push({label: 'Show all comments', value: 'Show all comments'}, {label: 'Show last comment', value: 'Show last comment'});
					field.value = field.definition.options[0];
				} else {
					let gridDataItem = gridAPIMap.get(item.id.toLowerCase());
					if (gridDataItem?.colDef?.cellType?.includes('linked') === true) fieldType = 'Linked Content';
					else fieldType = 'Lookup';
					field.definition.options.push({label: 'Show as linked content', value: 'Show as linked content'}, {label: 'Show as text', value: 'Show as text'});
					field.value = fieldType === 'Lookup' ? field.definition.options[1] : field.definition.options[0];
					field.definition.disableNestedOptions = true;
				}
				itemOptions = field;
			}
			let displayFieldTemplateParts = [];
			let displayFieldTemplate = null;

			if (Array.isArray(item.fieldCollectionConfig.displayFieldTemplateParts) === true && item.fieldCollectionConfig.displayFieldTemplateParts.length > 0) {
				let gridDataItem = gridAPIMap.get(item.id.toLowerCase());
				let linearPartsLookup = new Map();

				item.fieldCollectionConfig.displayFieldTemplateParts.forEach((field) => {
					linearPartsLookup.set(field.fieldId.toLowerCase(), field.fieldId);
					if (Array.isArray(field.additionalFilterFieldIds) === true) {
						field.additionalFilterFieldIds.forEach((additionalField) => {
							linearPartsLookup.set(additionalField.toLowerCase(), additionalField);
						});
					}
				});
				if (typeof gridDataItem?.colDef?.displayFieldTemplate == 'string') {
					displayFieldTemplate = gridDataItem?.colDef?.displayFieldTemplate;
					let lookupParts = displayFieldTemplate.match(/{{.+?(?=}})/g);
					if (Array.isArray(lookupParts) === true) {
						lookupParts.forEach((v) => {
							let fieldIds = v.match(/{{[^<]+|<<[^>]+(?=>>)/g) ?? [];

							for (let fieldId of fieldIds) {
								if (fieldId.includes('<<') === false) {
									fieldId = fieldId.replace('{{', '').trim();
									displayFieldTemplateParts.push({additionalFilterFieldIds: [], fieldId: fieldId, includeInSearchFilters: false});
								}
							}
						});
					}
				} else {
					if (item.fieldCollectionConfig.lookupCollectionId === 'AspNetUsers') {
						displayFieldTemplate = '{{FistName}} {{LastName}} {{Email}}';
						displayFieldTemplateParts = [
							{additionalFilterFieldIds: [], fieldId: 'FirstName', includeInSearchFilters: false},
							{additionalFilterFieldIds: [], fieldId: 'LastName', includeInSearchFilters: false},
							{additionalFilterFieldIds: [], fieldId: 'Email', includeInSearchFilters: false}
						];
					} else {
						displayFieldTemplate = '{{Title}}';
						displayFieldTemplateParts = [{additionalFilterFieldIds: [], fieldId: 'Title', includeInSearchFilters: false}];
					}
				}
			}

			let option = {
				label: item.displayName,
				value: {
					fieldId: item.id,
					name: item.displayName,
					lookupCollectionId: item.fieldCollectionConfig.lookupCollectionId,
					fieldType: fieldType,
					options: itemOptions,
					width: 30,
					displayFieldTemplateParts: displayFieldTemplateParts,
					displayFieldTemplate: displayFieldTemplate
				}
			};
			//we only set the value to the Map if it doesn't exist, but all options are returned
			if (_columnOptionsCache.has(`${collectionId}-${item.id}`) === false) {
				_columnOptionsCache.set(`${collectionId}-${item.id}`, option);
			}
			columnOptions.push(option);
		}
	}
	return columnOptions;
};

/**
 * TODO move this logic to the BE because we currently need to pass gridAPI data into this method
 * gets the column options from the gridData with a reverse lookup to data
 * gridAPIMap and dataMap are maps that are keyed off the lowercase fieldId
 * @param {Map} gridAPIMap
 * @param {String} collectionId
 * @param {Map} dataMap
 * @returns {Array}
 */
_csGridExport.getColumnOptionsFromGridData = (gridAPIMap, collectionId, dataMap) => {
	let columnOptions = [];
	//create a lowercase Map of the data items
	gridAPIMap.forEach((gridDataItem, key) => {
		let item = dataMap.get(gridDataItem.colDef.fieldId.toLowerCase());

		if (item.fieldCollectionConfig.hideFromPicker === false) {
			let itemOptions = null;
			//TODO - hardcoded options for now based on isComment and lookupCollectionId

			let fieldType = item.fieldCollectionConfig.isRepeater ? 'Repeater' : 'Core';

			if (item.fieldCollectionConfig.isComment === true || item.fieldCollectionConfig.lookupCollectionId != undefined) {
				let field = $cs_.deepClone(_csGridExport.createEmptySelectFieldObject());

				field.value = null;
				//Show as Lookup is Link content - Show as text is a Lookup using the displayTemplateField
				if (item.fieldCollectionConfig.isComment === true) {
					field.definition.options.push({label: 'Show all comments', value: 'Show all comments'}, {label: 'Show last comment', value: 'Show last comment'});
					field.value = field.definition.options[0];
				} else {
					if (gridDataItem?.colDef?.cellType?.includes('linked') === true) fieldType = 'Linked Content';
					else fieldType = 'Lookup';
					field.definition.options.push({label: 'Show as linked content', value: 'Show as linked content'}, {label: 'Show as text', value: 'Show as text'});
					field.value = fieldType === 'Lookup' ? field.definition.options[1] : field.definition.options[0];
					field.definition.disableNestedOptions = true;
				}
				itemOptions = field;
			}

			let option = {
				label: gridDataItem.colDef.headerName ?? gridDataItem.colDef.fieldId,
				value: {
					fieldId: item.id,
					colId: gridDataItem.colId,
					name: gridDataItem.colDef.headerName ?? gridDataItem.colDef.fieldId,
					lookupCollectionId: item.fieldCollectionConfig.lookupCollectionId,
					fieldType: fieldType,
					options: itemOptions,
					width: gridDataItem.actualWidth,
					displayFieldTemplateParts: gridDataItem.colDef.displayFieldTemplateParts,
					displayFieldTemplate: gridDataItem.colDef.displayFieldTemplate
				}
			};
			//we only set the value to the Map if it doesn't exist, but all options are returned
			if (_columnOptionsCache.has(`${collectionId}-${item.id}`) === false) {
				_columnOptionsCache.set(`${collectionId}-${item.id}`, option);
			}
			columnOptions.push(option);
		}
	});
	return columnOptions;
};

/**
 * Converts column options into the fieldCollection for export
 * @param {Array} fieldCollection
 * @returns {Array}
 */
_csGridExport.convertColumnOptionsToExportData = (fieldCollection) => {
	if (fieldCollection == undefined) return null;
	for (let field of fieldCollection) {
		switch (field?.options?.value?.value) {
			case 'Show all comments':
				field.exportLastComment = false;
				break;
			case 'Show last comment':
				field.exportLastComment = true;
				break;
			case 'Show as linked content':
				field.linkedFieldCollection = _csGridExport.convertColumnOptionsToExportData(field?.linkedFieldCollection?.fields?.[1]?.value);
				break;
			case 'Show as text':
				if (field?.displayFieldCollection?.fields?.[1]?.value?.[0]?.displayFieldTemplate != undefined) {
					field.displayFieldTemplate = field.displayFieldCollection.fields[1].value[0].displayFieldTemplate;
					field.displayFieldTemplateParts = field.displayFieldCollection.fields[1].value[0].displayFieldTemplateParts;
				}
				delete field.linkedFieldCollection;
				break;
		}
		field.type = field.type.replaceAll(' ', '');
		delete field.isShowSubfields;
		delete field.options;
		delete field.displayFieldCollection;
	}

	return fieldCollection.sort((a, b) => a.position - b.position);
};

/**
 * @public
 * @description
 * converts the fieldColumn data into usable data exported to the export form
 * @param {String} urlCollectionId
 * @returns {Map}
 */
_csGridExport.setAllColumnOptionsFromCollectionData = async (collectionId, gridAPIAllColumns, gridAPIDisplayColumns) => {
	_columnOptionsCache.clear();
	_collectionCache.clear();
	_collectionDisplayCache.clear();

	let urlCollectionAPI = `${$cs_.getAppNode().$getApiBaseUrl()}/api/SiteConfiguration/GetMultipleCollectionFields`;
	let payload = [collectionId];

	let getCollectionsGuid = $csNetworkCall.createFetchPostRequest(urlCollectionAPI, payload);
	let response = await $csNetworkCall.fetchRequest(getCollectionsGuid);

	if (response?.data?.[0] != undefined) {
		let gridAPIMap = new Map();
		let gridAPIDispalyMap = new Map();
		let responseDataMap = new Map();
		//columns
		gridAPIAllColumns.forEach((v) => {
			gridAPIMap.set(v.colId.toLowerCase(), v);
		});
		//displayColumns
		gridAPIDisplayColumns.forEach((v) => {
			gridAPIDispalyMap.set(v.colId.toLowerCase(), v);
		});
		//dataMap
		response.data[0].collectionFieldResponses.forEach((v) => {
			responseDataMap.set(v.id.toLowerCase(), v);
		});
		let columnOptions = _csGridExport.getColumnOptionsFromCollectionData(response.data[0].collectionFieldResponses, collectionId, gridAPIMap);
		_collectionCache.set(collectionId, columnOptions);

		let lookupOptionSet = new Set();
		for (let i = 0; i < columnOptions.length; i++) {
			let columnOption = columnOptions[i];
			if (columnOption?.value?.lookupCollectionId != undefined) {
				lookupOptionSet.add(columnOption.value.lookupCollectionId);
			}
		}
		//create a displayONLY cache
		let displayColumnOptions = _csGridExport.getColumnOptionsFromGridData(gridAPIDispalyMap, collectionId, responseDataMap);
		_collectionDisplayCache.set(collectionId, displayColumnOptions);

		payload = Array.from(lookupOptionSet);
		getCollectionsGuid = $csNetworkCall.createFetchPostRequest(urlCollectionAPI, payload);
		response = await $csNetworkCall.fetchRequest(getCollectionsGuid);
		if (Array.isArray(response?.data) === true) {
			for (let i = 0; i < response?.data.length; i++) {
				//the collection cache can reference the same collection as first level columns
				if (_collectionCache.has(payload[i]) === false) {
					let nestedColumnOptions = _csGridExport.getColumnOptionsFromCollectionData(response.data[i].collectionFieldResponses, payload[i], gridAPIMap);
					_collectionCache.set(payload[i], nestedColumnOptions);
				}
			}
		}
	}
};

/**
 *
 * @param {Array} fieldCollection
 * @param {String} collectionId
 * @param {Object} subformDefinition
 * @param {Object} gridAPI
 * @returns
 */
_csGridExport.populateFieldChoiceValueWithAllOptions = (fieldCollection = [], collectionId, subformDefinition, gridAPI) => {
	let choiceFields = [];

	for (let field of fieldCollection) {
		$csGridExport.addFieldToChoiceValue(field, choiceFields, subformDefinition, gridAPI, collectionId);
	}
	return choiceFields.length > 0 ? choiceFields : null;
};

/**
 * returns a linearArray of displayFieldTemplate lookup parts for the construction of the diaplay fields options object
 * @param {String} displayFieldTemplate
 * @param {String} parentCollectionId
 * @returns
 */
_csGridExport.getDisplayLookupParts = (displayFieldTemplate, parentCollectionId) => {
	let lookupParts = displayFieldTemplate.match(/{{.+?(?=}})/g) ?? [];
	let linearLookupParts = [];

	lookupParts.forEach((v) => {
		let fieldIds = v.match(/{{[^<]+|<<[^>]+(?=>>)/g) ?? [];
		let nestedLookupCollectionId = v.includes('<<') === true ? fieldIds.shift().replace('{{', '').trim() : undefined;
		//the nestedLookupCollectionId needs to be bounced off the collection in the parentCollection to find the lookupCollectionId
		if (nestedLookupCollectionId != undefined) {
			nestedLookupCollectionId = $csGridExport.getColumnField(nestedLookupCollectionId, parentCollectionId)?.value?.lookupCollectionId;
		}
		for (let fieldId of fieldIds) {
			fieldId = fieldId.replace('{{', '').trim();
			fieldId = fieldId.replace('<<', '').trim();
			linearLookupParts.push({collectionId: nestedLookupCollectionId ?? parentCollectionId, fieldId: fieldId});
		}
		//add default fieldIds
		if (nestedLookupCollectionId != undefined && fieldIds.length === 0) {
			if (nestedLookupCollectionId === 'AspNetUsers') linearLookupParts.push({collectionId: nestedLookupCollectionId, fieldId: 'FirstName'}, {collectionId: nestedLookupCollectionId, fieldId: 'LastName'}, {collectionId: nestedLookupCollectionId, fieldId: 'Email'});
			else linearLookupParts.push({collectionId: nestedLookupCollectionId, fieldId: 'Title'});
		}
	});
	return linearLookupParts;
};

//PUBLIC

const $csGridExport = {};
/**
 * creates the cache for the collections, all fields form, and displayed fields form
 * @param {String} collectionId
 * @param {Object} choiceSubformDefinition
 * @param {Array} gridAPIDisplayColumns
 */
$csGridExport.createAllColumnFieldsAndVisibleColumnFieldsAndSaveToCache = async (collectionId, choiceSubformDefinition, gridAPIAllColumns, gridAPIDisplayColumns) => {
	await _csGridExport.setAllColumnOptionsFromCollectionData(collectionId, gridAPIAllColumns, gridAPIDisplayColumns);

	let allColumnsFieldValue = _csGridExport.populateFieldChoiceValueWithAllOptions(_collectionCache.get(collectionId), collectionId, choiceSubformDefinition, gridAPIAllColumns);
	let displayedColumns = _csGridExport.populateFieldChoiceValueWithAllOptions(_collectionDisplayCache.get(collectionId), collectionId, choiceSubformDefinition, gridAPIDisplayColumns);

	_columnFieldFormsCache.set(collectionId, {allColumnFieldsProject: $cs_.deepClone(allColumnsFieldValue), allColumnFieldsGrid: $cs_.deepClone(displayedColumns)});
};

/**
 * returns the cached collectionId forms for all the fields and displayed fields
 * this cache is updated on every export open
 * @param {String} collectionId
 * @returns {Array}
 */
$csGridExport.getColumnFieldsForm = (collectionId) => {
	return $cs_.deepClone(_columnFieldFormsCache.get(collectionId));
};

/**
 * returns the cached fieldId from a combination of collectionId and fieldId from the cache
 * this cache is updated on every export open
 * @param {String} fieldId
 * @param {String} collectionId
 * @returns {Object}
 */
$csGridExport.getColumnField = (fieldId, collectionId) => {
	return $cs_.deepClone(_columnOptionsCache.get(`${collectionId}-${fieldId}`));
};

/**
 * payload transformer - converts exportForms to configUI form structure
 * ensure we have a clone of the formsArray as we mutate the values
 * if we have a pdf export, check the export format is visible and add printSize and pageOrientation fields to the payload
 * @param {Array} formsArray
 */
$csGridExport.createExportFormTemplatePayload = (formsArray, userId) => {
	formsArray = $cs_.deepClone(formsArray);
	let prepareYouExport = formsArray.find((v) => v.id === 'prepare-your-export');
	let templateForm = formsArray.find((v) => v.id === 'customise-template');
	let saveForm = formsArray.find((v) => v.id === 'save');
	let exportForm = formsArray.find((v) => v.id === 'export');

	let useExportTemplate = prepareYouExport.fields.find((v) => v.id === 'use-existing-export')?.value?.exportTemplate;
	let isSaveExisting = saveForm.fields.find((v) => v.id === 'save-existing-new')?.value === 'save over existing';
	let saveExportTemplate = saveForm.fields.find((v) => v.id === 'save-existing-export')?.value?.exportTemplate;

	let printSize = null;
	let pageOrientation = null;

	if (templateForm !== undefined) {
		let collectionId = templateForm.fields[0].definition.lookup;
		let name = isSaveExisting === true ? saveExportTemplate?.name ?? null : saveForm.fields.find((v) => v.id === 'save-new-template')?.value?.trim() ?? null;
		let type = useExportTemplate?.type ?? prepareYouExport.fields.find((v) => v.id === 'export-type')?.value;
		let id = isSaveExisting === true ? saveExportTemplate?.id ?? null : null;
		let fieldCollection = $cs_.deepClone(templateForm.fields[1].value);
		let mergeLinkedItemCells = exportForm.fields.find((v) => v.id === 'file-structure')?.value === '';
		if (useExportTemplate != undefined) {
			printSize = useExportTemplate?.printSize;
			pageOrientation = useExportTemplate?.pageOrientation;
		} else if (prepareYouExport.fields.find((v) => v.id === 'export-format' && v.isVisible === true) != undefined) {
			printSize = prepareYouExport.fields.find((v) => v.id === 'export-format' && v.isVisible === true)?.value ?? null;
			pageOrientation = 'landscape';
		}

		let payload = new ExportPayload({
			collectionId: collectionId,
			documentTitle: `${name} - ${collectionId}`,
			fieldCollection: _csGridExport.convertColumnOptionsToExportData(fieldCollection),
			id: id,
			mergeLinkedItemCells: mergeLinkedItemCells,
			name: name,
			printSize: printSize,
			pageOrientation: pageOrientation,
			type: type,
			owner: userId
		});
		return payload;
	}
};

/**
 * field collection
 * @param {Object} fieldCollection
 * @param {Object} subformDefinition //null for nested fields
 */
$csGridExport.addFieldDataFromExportFieldCollectionData = (exportTemplate, fieldCollection, subformDefinition, recursivePassThrough = {}) => {
	let choiceFields = [];
	let sourceCollectionId = recursivePassThrough.sourceCollectionId ?? exportTemplate.collectionId;

	for (let i = 0; i < fieldCollection.length; i++) {
		let field = fieldCollection[i];
		let cachedCollectionField = $csGridExport.getColumnField(field.fieldId, sourceCollectionId)?.value;

		let newChoiceItem = {
			fieldId: field.fieldId,
			position: choiceFields.length,
			headerName: field.headerName ?? cachedCollectionField?.name,
			type: field.type === 'LinkedContent' ? 'Linked Content' : field.type,
			isShowSubfields: false,
			width: field.width ?? 30,
			options: $cs_.deepClone(cachedCollectionField?.options) ?? null,
			lookupCollectionId: field.lookupCollectionId,
			linkedFieldCollection: field.lookupCollectionId != undefined && subformDefinition != undefined ? $cs_.deepClone(subformDefinition) : null,
			displayFieldCollection: field.lookupCollectionId != undefined && subformDefinition != undefined ? $cs_.deepClone(subformDefinition) : null,
			displayFieldTemplate: subformDefinition != undefined ? field.displayFieldTemplate ?? null : recursivePassThrough.displayFieldTemplate ?? null,
			displayFieldTemplateParts: subformDefinition != undefined ? $cs_.deepClone(field.displayFieldTemplateParts) ?? [] : recursivePassThrough.displayFieldTemplateParts ?? []
		};
		if (field.type === 'Lookup' && newChoiceItem.displayFieldCollection != undefined && newChoiceItem.displayFieldTemplate != undefined && Array.isArray(newChoiceItem.displayFieldTemplateParts)) {
			let linearLookupParts = _csGridExport.getDisplayLookupParts(newChoiceItem.displayFieldTemplate, sourceCollectionId);
			let combinedCollectionObject = [];

			//Finally extract the linearised lookup parts into column fields
			linearLookupParts.forEach((lookupObj) => {
				if (lookupObj.fieldId != undefined && lookupObj.collectionId != undefined) {
					let field = $csGridExport.getColumnField(lookupObj.fieldId, lookupObj.collectionId)?.value;

					if (field != undefined) {
						combinedCollectionObject.push({
							fieldId: field?.fieldId,
							headerName: field?.name,
							type: field?.fieldType,
							width: field?.width
						});
					}
				}
			});

			newChoiceItem.displayFieldCollection.fields[1].value = $csGridExport.addFieldDataFromExportFieldCollectionData(exportTemplate, combinedCollectionObject, null, {
				displayFieldTemplate: newChoiceItem.displayFieldTemplate,
				displayFieldTemplateParts: newChoiceItem.displayFieldTemplateParts,
				sourceCollectionId: newChoiceItem.lookupCollectionId
			});
		}
		if (field.exportLastComment === true && newChoiceItem?.options?.value?.value === 'Show all comments') {
			let lastCommentOption = newChoiceItem.options.definition.options.find((v) => v.value === 'Show last comment');
			if (lastCommentOption != undefined) newChoiceItem.options.value = $cs_.deepClone(lastCommentOption);
		}
		if (field.type === 'LinkedContent' && newChoiceItem.linkedFieldCollection != undefined && Array.isArray(newChoiceItem?.options?.definition?.options) === true) {
			//let cachedCollectionField = cachedCollectionFields.allColumnFieldsProject.find((v) => v.fieldId === field.fieldId);
			let showAsLinkedContentOption = newChoiceItem.options.definition.options.find((v) => v.value === 'Show as linked content');
			if (showAsLinkedContentOption != undefined) newChoiceItem.options.value = $cs_.deepClone(showAsLinkedContentOption);

			if (Array.isArray(field.linkedFieldCollection) && field.linkedFieldCollection.length > 0) {
				newChoiceItem.linkedFieldCollection.fields[1].value = $csGridExport.addFieldDataFromExportFieldCollectionData(exportTemplate, field.linkedFieldCollection, null, {sourceCollectionId: newChoiceItem.lookupCollectionId});
			}
		}

		if (field.lookupCollectionId != undefined && newChoiceItem.linkedFieldCollection != undefined) {
			newChoiceItem.linkedFieldCollection.fields[0].definition.lookup = field.lookupCollectionId;
		}
		choiceFields.push(newChoiceItem);
	}
	return choiceFields;
};

/**
 * returns field data from exportTemplate data as an Array of options
 */
$csGridExport.getFieldDataFromExportTemplateData = (exportTemplates, subformDefinition) => {
	let options = [];
	if (Array.isArray(exportTemplates) === true) {
		for (let exportTemplate of exportTemplates) {
			let choiceFields = $csGridExport.addFieldDataFromExportFieldCollectionData(exportTemplate, exportTemplate.fieldCollection, subformDefinition);
			options.push({label: exportTemplate.name, value: exportTemplate.id, choiceFields: choiceFields, exportTemplate: exportTemplate});
		}
	}
	return options;
};

/**
 * adds a choiceItem to a passed in choiceFields array
 * gridAPIColumns is optional and is used to recrusively add all the grid lookups and linkedcontent
 * parentCollectionId is needed when the girdAPIColumns is used to be able to lookup the displayFieldTemplate nested lookups
 * @param {Object} field
 * @param {Array} choiceFields
 * @param {Object} subformDefinition
 * @param {Array} gridAPIColumns [optional]
 * @param {Array} fieldCollection [optional]
 * @returns
 */
$csGridExport.addFieldToChoiceValue = (field, choiceFields, subformDefinition, gridAPIColumns, parentCollectionId) => {
	let {fieldId, name, fieldType, width, lookupCollectionId, options, displayFieldTemplateParts, displayFieldTemplate} = field.value;
	let subform = null;
	let displayTemplateSubform = null;

	if (lookupCollectionId != undefined) {
		subform = $cs_.deepClone(subformDefinition) ?? null;

		if (subform != undefined) {
			subform.fields[0].definition.lookup = lookupCollectionId;
			displayTemplateSubform = $cs_.deepClone(subform);
		} else {
			lookupCollectionId = null;
		}
	}

	//LINKED FIELDS - recurse subform fields - do not provide the gridAPIColumns to prevent further recursion - only one level is supported
	if (Array.isArray(gridAPIColumns) === true && lookupCollectionId != undefined && subform != undefined) {
		let colId = field.value.colId ?? fieldId;
		let gridColumn = gridAPIColumns.find((v) => v.colId === colId); //TODO is the only way to check for linkedFields in the GRIDAPI textFields

		if (gridColumn != undefined) {
			let textFields = gridColumn?.colDef?.linkedItemsForm?.textFields ?? [];

			for (let textField of textFields) {
				let linkedContentField = $csGridExport.getColumnField(textField.fieldId, lookupCollectionId);

				if (linkedContentField != undefined) {
					if (subform.fields[1].value == undefined) subform.fields[1].value = [];
					$csGridExport.addFieldToChoiceValue(linkedContentField, subform.fields[1].value, subformDefinition);
				}
			}
		}
	}
	//LOOKUP FIELDS - recurse subform fields - do not provide the gridAPIColumns to prevent further recursion - only one level is supported
	if (Array.isArray(gridAPIColumns) === true && lookupCollectionId && displayFieldTemplate != undefined) {
		let linearLookupParts = _csGridExport.getDisplayLookupParts(displayFieldTemplate, parentCollectionId);

		//extract the linearised lookup parts into column fields
		linearLookupParts.forEach((lookupObj) => {
			if (lookupObj.fieldId != undefined && lookupObj.collectionId != undefined) {
				let displayField = $csGridExport.getColumnField(lookupObj.fieldId, lookupObj.collectionId);

				if (displayField != undefined) {
					if (displayTemplateSubform.fields[1].value == undefined) displayTemplateSubform.fields[1].value = [];
					$csGridExport.addFieldToChoiceValue(displayField, displayTemplateSubform.fields[1].value, subformDefinition);
				}
			}
		});
	}

	let newChoiceItem = {
		fieldId: fieldId,
		position: choiceFields.length,
		headerName: name,
		type: fieldType,
		isShowSubfields: false,
		width: width,
		options: $cs_.deepClone(options),
		lookupCollectionId: lookupCollectionId,
		linkedFieldCollection: subform,
		displayFieldCollection: displayTemplateSubform,
		displayFieldTemplate: displayFieldTemplate,
		displayFieldTemplateParts: displayFieldTemplateParts
	};
	//the displayFieldTemplate is the string interpolation value and will only be set for lookups with a grid

	choiceFields.push(newChoiceItem);
};

/**
 * returns the collection cache
 */
$csGridExport.getCollectionCache = () => {
	return _collectionCache;
};

/**
 * sets the url base on load of app
 * @param {String} urlBase
 */
$csGridExport.setUrlBase = (urlBase) => {
	_urlBase = urlBase;
};

//GRID EXPORT PUSH NOTIFICATIONS

const $csGridExportPushNotification = {};

/**
 * @public
 * @description
 * creates a new export and makes a request to the initial StartGridExportBackgroundService API endpoint
 * dataOptions are the payload
 * if this method fails we simple do not push a new request into _pendingExportArray
 * @param {Object} dataOptions
 * @param {String} [icon] optional - defaults to 'file-download'
 */
$csGridExportPushNotification.createNewExport = async (dataOptions, icon = 'file-download') => {
	let dataUrl = `${_urlBase}/api/GenericObjectController/StartGridExportBackgroundService`;
	let newExport = {exportId: dataOptions.exportId, fileName: dataOptions.fileName, icon: icon, exportType: dataOptions.parentRowDisplayOptions, respectTabsFiltering: dataOptions.respectTabsFiltering};

	newExport.guid = _gridExportGuid;
	_gridExportGuid += 5;
	//Make the network call and consume the promise
	let networkCallGuid = $csNetworkCall.createFetchPostRequest(dataUrl, dataOptions);
	let response = await $csNetworkCall.fetchRequest(networkCallGuid);
	if (response != undefined) {
		let exportJobId = await response.data;
		let pendingExportArray = sessionStorage['pendingGridExports'] != undefined ? JSON.parse(sessionStorage['pendingGridExports']) : [];

		newExport.exportJobId = exportJobId;

		pendingExportArray.push($cs_.deepClone(newExport));
		sessionStorage['pendingGridExports'] = JSON.stringify(pendingExportArray);
	}
};

/**
 * @public
 * @description
 * Shows how many exports are in progress.
 *
 * @returns {Integer}
 */
$csGridExportPushNotification.getPendingCount = () => {
	let pendingExportArray = sessionStorage['pendingGridExports'] != undefined ? JSON.parse(sessionStorage['pendingGridExports']) : [];
	return pendingExportArray.length;
};

/**
 * @public
 * @description
 * polls the session storage pending array for their current state
 */
$csGridExportPushNotification.pollPendingExportsForTheirCurrentStatus = async () => {
	let pendingExportArray = sessionStorage['pendingGridExports'] != undefined ? JSON.parse(sessionStorage['pendingGridExports']) : [];
	let promiseResults = [];
	let dataUrl = `${_urlBase}/api/GenericObjectController/CheckExportStatus`;

	for (let i = pendingExportArray.length - 1; i >= 0; i--) {
		let pendingExport = pendingExportArray[i];
		pendingExport.networkExportStatusGuid = $csNetworkCall.createFetchGetRequest(`${dataUrl}/${pendingExport.exportJobId}`);
		promiseResults.push(_csGridExport.consumeExportPromise(pendingExport));
	}

	//await all of the promises to be settled and handled any that failed by setting their status to 'Failed'
	let settledResults = await Promise.allSettled(promiseResults);

	for (let i = settledResults.length - 1; i >= 0; i--) {
		if (settledResults[i].status === 'rejected' || settledResults[i].value === 'Failed') $csGridExportPushNotification.deletePendingExport(pendingExportArray[i].exportJobId);
		else if (settledResults[i].value === 'Succeeded') $csGridExportPushNotification.moveExportFromPendingToCompleted(pendingExportArray[i].exportJobId);
	}
};

/**
 * @public
 * @description
 * deletes a pending export from the session storage pending list
 * @param {String | Number} exportJobId
 * @returns {Boolean}
 */
$csGridExportPushNotification.deletePendingExport = (exportJobId) => {
	let pendingExportArray = sessionStorage['pendingGridExports'] != undefined ? JSON.parse(sessionStorage['pendingGridExports']) : [];
	let pendingExportIndex = pendingExportArray.findIndex((v) => v.exportJobId === exportJobId);

	if (pendingExportIndex !== -1) {
		pendingExportArray.splice(pendingExportIndex, 1);

		if (pendingExportArray.length === 0) sessionStorage.removeItem('pendingGridExports');
		else sessionStorage.setItem('pendingGridExports', JSON.stringify(pendingExportArray));
	}
	return pendingExportArray.length !== 0;
};

/**
 * @public
 * @description
 * Called by the push notification module
 * moves an export from the pending list to the completed list
 * returns a Boolean which is true if there are still pendingExports remaining false if exporting has fully completed
 * @param {String | Number} exportJobId
 * @returns {Boolean}
 */
$csGridExportPushNotification.moveExportFromPendingToCompleted = (exportJobId) => {
	let pendingExportArray = sessionStorage['pendingGridExports'] != undefined ? JSON.parse(sessionStorage['pendingGridExports']) : [];
	let completedExportArray = sessionStorage['completedGridExports'] != undefined ? JSON.parse(sessionStorage['completedGridExports']) : [];

	let pendingExportIndex = pendingExportArray.findIndex((v) => v.exportJobId === exportJobId);
	if (pendingExportIndex !== -1) {
		let completedExport = pendingExportArray.splice(pendingExportIndex, 1)[0];

		if (pendingExportArray.length === 0) sessionStorage.removeItem('pendingGridExports');
		else sessionStorage.setItem('pendingGridExports', JSON.stringify(pendingExportArray));

		completedExportArray.push(completedExport);
		sessionStorage.setItem('completedGridExports', JSON.stringify(completedExportArray));
	}
	return pendingExportArray.length !== 0;
};

/**
 * @public
 * @description
 * returns a list of the completed exports for use in cs-header
 * @returns {Array}
 */
$csGridExportPushNotification.getDownloadList = () => {
	let exportOptions = [];
	let completedExportArray = sessionStorage['completedGridExports'] != undefined ? JSON.parse(sessionStorage['completedGridExports']) : [];

	if (completedExportArray.length > 1) {
		exportOptions.push({
			exportJobId: -1,
			fileName: '',
			text: 'Download All',
			icon: 'file-download'
		});
	}

	let completedExports = completedExportArray.map((v) => {
		return {
			exportJobId: v.exportJobId,
			fileName: v.fileName,
			text: v.fileName,
			icon: typeof v.icon === 'string' ? v.icon : 'file-download'
		};
	});
	exportOptions.push(...completedExports);
	return exportOptions;
};

/**
 * @public
 * @description
 * downloads the export in the list by exportJobId
 * if exportJobId is -1 it downloads ALL the completedExports that are available
 * @param {*} exportJobId
 * @returns
 */
$csGridExportPushNotification.downloadExports = async (exportJobId = -1) => {
	try {
		let result = true;
		let isSuccess = true;
		let completedExportArray = sessionStorage['completedGridExports'] != undefined ? JSON.parse(sessionStorage['completedGridExports']) : [];

		if (exportJobId === -1) {
			//DOWNLOAD ALL
			sessionStorage.removeItem('completedGridExports');

			for (let i = 0; i < completedExportArray.length; i++) {
				isSuccess = await _csGridExport.downloadExportFile(completedExportArray[i], true);
				await $cs_.wait(500);
			}
		} else {
			//download specific exportJobId
			let i = completedExportArray.findIndex((v) => v.exportJobId === exportJobId);
			if (i !== -1) {
				let download = completedExportArray[i];
				completedExportArray.splice(i, 1);
				if (completedExportArray.length > 0) sessionStorage.setItem('completedGridExports', JSON.stringify(completedExportArray));
				else sessionStorage.removeItem('completedGridExports');
				isSuccess = await _csGridExport.downloadExportFile(download);
			} else {
				result = false;
			}
		}
		return result === false || isSuccess === false ? false : true;
	} catch (error) {
		sessionStorage.removeItem('completedGridExports');
		return false;
	}
};

export default $csGridExport;
export {$csGridExportPushNotification};
